import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Server, Plus, RefreshCw, Calendar, Clock, CheckCircle2,
  XCircle, AlertCircle, ShoppingCart, Package, ChevronRight,
  Eye, Loader2, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_LABELS = {
  monthly: 'Monthly', quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly', yearly: 'Yearly',
  two_years: '2 Years', three_years: '3 Years',
};

const STATUS_CONFIG = {
  pending:         { label: 'Pending',         className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  pending_payment: { label: 'Pending Payment', className: 'bg-orange-100 text-orange-800 border-orange-200' },
  active:          { label: 'Active',          className: 'bg-green-100  text-green-800  border-green-200'  },
  expired:         { label: 'Expired',         className: 'bg-slate-100  text-slate-500  border-slate-200'  },
  cancelled:       { label: 'Cancelled',       className: 'bg-red-100    text-red-700    border-red-200'    },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.className}`}>{cfg.label}</span>;
}

// ── Order Detail Drawer ────────────────────────────────────────────────────
function OrderDetailModal({ order, onClose }) {
  if (!order) return null;
  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="self-order-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-600" /> Service Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-sm">Status</span>
            <StatusBadge status={order.status} />
          </div>
          <div className="bg-[#06b6d408] rounded-lg p-4 space-y-3 text-sm">
            {[
              ['Product',       order.product_name],
              ['Category',      order.product_category || '—'],
              ['Billing Cycle', BILLING_LABELS[order.billing_cycle] || order.billing_cycle],
              ['Quantity',      order.quantity],
              ['Unit Price',    `₹${Number(order.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
              ['Subtotal',      `₹${Number(order.subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
              ['GST',           `₹${Number(order.gst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
              ['Total Amount',  `₹${Number(order.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium text-slate-800">{v}</span>
              </div>
            ))}
          </div>
          {(order.start_date || order.renewal_date) && (
            <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
              {order.start_date && (
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Start Date</span>
                  <span className="font-medium">{new Date(order.start_date).toLocaleDateString('en-IN')}</span>
                </div>
              )}
              {order.renewal_date && (
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Renewal Date</span>
                  <span className="font-medium">{new Date(order.renewal_date).toLocaleDateString('en-IN')}</span>
                </div>
              )}
            </div>
          )}
          {order.notes && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Notes</p>
              <p className="text-sm text-slate-700 bg-slate-50 rounded p-2">{order.notes}</p>
            </div>
          )}
          <p className="text-xs text-slate-400">Order ID: {order.id} · Placed on {new Date(order.created_at).toLocaleDateString('en-IN')}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── New Order Modal ────────────────────────────────────────────────────────
function NewOrderModal({ open, onClose, onSuccess }) {
  const [categories, setCategories] = useState([]);
  const [pricingMap, setPricingMap] = useState({});   // productId → effective_base_prices
  const [loadingCats, setLoadingCats] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Load categories + full pricing map when modal opens
  useEffect(() => {
    if (!open) return;
    setLoadingCats(true);
    setSelectedCategory(null);
    setSelectedProduct(null);
    setProducts([]);

    Promise.all([
      axios.get(`${API}/reseller/visible-categories`),
      axios.get(`${API}/reseller/pricing`),
    ]).then(([catRes, priceRes]) => {
      setCategories((catRes.data || []).filter(c => c.visible_product_count > 0));
      // Build map: productId → effective_base_prices (partner-specific)
      const map = {};
      for (const row of (priceRes.data || [])) {
        if (row.product?.id) map[row.product.id] = row.effective_base_prices;
      }
      setPricingMap(map);
    }).catch(() => toast.error('Failed to load catalog'))
      .finally(() => setLoadingCats(false));
  }, [open]);

  // Step 2: Load products when a category is selected
  useEffect(() => {
    if (!selectedCategory) { setProducts([]); return; }
    setLoadingProds(true);
    setSelectedProduct(null);
    axios.get(`${API}/reseller/visible-products?category_id=${selectedCategory.id}`)
      .then(r => setProducts(r.data || []))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoadingProds(false));
  }, [selectedCategory]);

  // Resolve partner-specific price for a product + cycle
  const getPrice = (product, cycle) => {
    if (!product) return 0;
    const ep = pricingMap[product.id];
    if (ep && ep[cycle] != null) return ep[cycle];
    // Fallback: product.billing_prices → base_price
    const bp = product.billing_prices || {};
    return bp[cycle] || product.base_price || 0;
  };

  const unitPrice = getPrice(selectedProduct, billingCycle);
  const gstAmt   = Math.round(unitPrice * 0.18 * 100) / 100;
  const total    = Math.round((unitPrice + gstAmt) * 100) / 100;

  const handlePlace = async () => {
    if (!selectedProduct) { toast.error('Please select a product'); return; }
    if (!selectedCategory) { toast.error('Please select a category'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/self-orders`, {
        product_id:       selectedProduct.id,
        product_name:     selectedProduct.name,
        product_category: selectedCategory.name || '',
        billing_cycle:    billingCycle,
        quantity:         1,
        unit_price:       unitPrice,
        notes:            notes.trim() || undefined,
      });
      toast.success('Order placed! Admin will activate it shortly.');
      onSuccess();
      onClose();
      setSelectedCategory(null); setSelectedProduct(null);
      setBillingCycle('monthly'); setNotes('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-self-order-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-600" /> Order a Service for Yourself
          </DialogTitle>
          <p className="text-sm text-slate-500">Priced at your partner rate. Not linked to any client.</p>
        </DialogHeader>

        {loadingCats ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-5 py-2">

            {/* ── STEP 1: Category ─────────────────────────────── */}
            <div className="!mb-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="h-5 w-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">1</span>
                <Label className="font-semibold">Select Category</Label>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    data-testid={`category-${cat.id}`}
                    onClick={() => setSelectedCategory(cat)}
                    className={`border rounded-lg p-3 text-left transition-all ${
                      selectedCategory?.id === cat.id
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <p className={`font-medium text-sm ${selectedCategory?.id === cat.id ? 'text-blue-700' : 'text-slate-800'}`}>
                      {cat.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{cat.visible_product_count} plan{cat.visible_product_count !== 1 ? 's' : ''}</p>
                  </button>
                ))}
                {categories.length === 0 && (
                  <p className="col-span-3 text-sm text-slate-400 py-4 text-center">No categories available</p>
                )}
              </div>
            </div>

            {/* ── STEP 2: Product — shown only after category is selected ── */}
            {selectedCategory && (
              <div className="my-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-5 w-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">2</span>
                  <Label className="font-semibold">Select Plan — <span className="text-blue-600">{selectedCategory.name}</span></Label>
                </div>

                {loadingProds ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : products.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">No products in this category</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                    {products.map(p => {
                      const monthlyPrice = getPrice(p, 'monthly');
                      return (
                        <div
                          key={p.id}
                          data-testid={`product-option-${p.id}`}
                          onClick={() => {
                            setSelectedProduct(p);
                            // Reset billing cycle to first enabled one for this product
                            const enabledCycles = p.enabled_billing_cycles && p.enabled_billing_cycles.length > 0
                              ? p.enabled_billing_cycles
                              : Object.keys(BILLING_LABELS);
                            if (!enabledCycles.includes(billingCycle)) {
                              setBillingCycle(enabledCycles[0] || 'monthly');
                            }
                          }}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            selectedProduct?.id === p.id
                              ? 'border-blue-500 bg-blue-50 shadow-sm'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <Package className={`h-4 w-4 mt-0.5 flex-shrink-0 ${selectedProduct?.id === p.id ? 'text-blue-600' : 'text-slate-400'}`} />
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800 leading-tight">{p.name}</p>
                              <p className="text-xs font-semibold text-blue-700 mt-1">
                                ₹{monthlyPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}/mo
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Product Info Card (shows after product selection) ── */}
            {selectedProduct && (() => {
              const SKIP_FIELDS = new Set(['id','_id','is_active','created_at','updated_at','category_id','base_price','billing_prices','enabled_billing_cycles']);
              const entries = Object.entries(selectedProduct).filter(([k,v]) => !SKIP_FIELDS.has(k) && v != null && v !== '' && k !== 'name');
              return (
                <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3.5 text-sm space-y-2.5" data-testid="product-info-card">
                  {entries.map(([key, val]) => {
                    const label = key.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
                    if (Array.isArray(val) && val.length) return (
                      <div key={key}>
                        <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
                        <ul className="space-y-0.5 ml-3">{val.map((v,i)=>(
                          <li key={i} className="text-slate-700 flex items-start gap-1.5">
                            <span className="text-blue-400 mt-0.5 shrink-0">•</span><span>{String(v)}</span>
                          </li>
                        ))}</ul>
                      </div>
                    );
                    if (val && typeof val === 'object' && !Array.isArray(val)) return (
                      <div key={key}>
                        <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">{Object.entries(val).map(([k2,v2])=>(
                          <div key={k2} className="flex justify-between col-span-1">
                            <span className="text-slate-500 capitalize">{k2.replace(/_/g,' ')}:</span>
                            <span className="text-slate-800 font-medium ml-1">{String(v2)}</span>
                          </div>
                        ))}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── STEP 3: Billing cycle + price preview ─────────── */}
            {selectedProduct && (
              <>
                <div className="my-2">
                  <Label className="mb-2 block font-semibold">Billing Cycle</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(BILLING_LABELS)
                      .filter(([v]) => !selectedProduct.enabled_billing_cycles || selectedProduct.enabled_billing_cycles.length === 0 || selectedProduct.enabled_billing_cycles.includes(v))
                      .map(([v, l]) => {
                      const price = getPrice(selectedProduct, v);
                      return (
                        <button
                          key={v}
                          data-testid={`cycle-${v}`}
                          onClick={() => setBillingCycle(v)}
                          className={`border rounded-lg p-2.5 text-left transition-all ${
                            billingCycle === v
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-blue-300'
                          }`}
                        >
                          <p className={`text-xs font-semibold ${billingCycle === v ? 'text-blue-700' : 'text-slate-600'}`}>{l}</p>
                          <p className="text-sm font-bold text-slate-900 mt-0.5">₹{price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Price summary */}
                <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-1.5 border">
                  <div className="flex justify-between text-slate-600"><span>Unit Price</span><span>₹{unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-slate-600"><span>GST (18%)</span><span>₹{gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between font-bold text-slate-900 border-t pt-1.5 mt-1 text-base">
                    <span>Total Payable</span><span>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="my-2">
                  <Label className="mb-1.5 block text-slate-600">Notes / Requirements (optional)</Label>
                  <Textarea
                    placeholder="e.g. preferred config, domain, server location…"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    data-testid="self-order-notes"
                    className="text-sm"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handlePlace}
                disabled={submitting || !selectedProduct}
                data-testid="place-self-order-btn"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Placing…</> : 'Place Order'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function MyServices() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [renewingId, setRenewingId] = useState(null);  // order id currently being renewed
  const [credOrder, setCredOrder] = useState(null);   // order whose credentials to show
  const [showCredPass, setShowCredPass] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null); // order id being invoiced

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/partner/self-orders`);
      setOrders(r.data || []);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRenew = async (order) => {
    setRenewingId(order.id);
    try {
      await axios.post(`${API}/partner/self-orders/${order.id}/renew`);
      toast.success(`Renewal order placed for ${order.product_name}. Admin will activate it shortly.`);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to renew order');
    } finally {
      setRenewingId(null);
    }
  };

  const handleDownloadInvoice = async (order) => {
    setDownloadingInvoice(order.id);
    try {
      const res = await axios.get(`${API}/partner/self-orders/${order.id}/invoice`);
      const { pdf_data, filename } = res.data;
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_data}`;
      link.download = filename || `Invoice_${order.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const filtered = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter);

  const stats = {
    total:     orders.length,
    active:    orders.filter(o => o.status === 'active').length,
    pending:   orders.filter(o => o.status === 'pending').length,
    expired:   orders.filter(o => o.status === 'expired').length,
  };

  const TABS = [
    { id: 'services', label: 'My Services' },
    { id: 'order',    label: 'Order New Service' },
  ];

  return (
    <DashboardLayout role={user?.role}>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              My Services
            </h1>
            <p className="text-slate-500 text-sm mt-1">Services ordered for your own use — separate from your client orders</p>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            onClick={() => setNewOrderOpen(true)}
            data-testid="order-new-service-btn"
          >
            <Plus className="h-4 w-4" /> Order New Service
          </Button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total',   value: stats.total,   icon: Package,       color: 'text-slate-700',  bg: 'bg-slate-50'  },
            { label: 'Active',  value: stats.active,  icon: CheckCircle2,  color: 'text-green-700',  bg: 'bg-green-50'  },
            { label: 'Pending', value: stats.pending, icon: AlertCircle,   color: 'text-yellow-700', bg: 'bg-yellow-50' },
            { label: 'Expired', value: stats.expired, icon: XCircle,       color: 'text-slate-500',  bg: 'bg-slate-50'  },
          ].map(s => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className={`p-4 ${s.bg} rounded-lg flex items-center gap-3`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter + Refresh */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[['all','All'], ['pending','Pending'], ['active','Active'], ['expired','Expired'], ['cancelled','Cancelled']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setStatusFilter(v)}
                data-testid={`filter-${v}`}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  statusFilter === v ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchOrders} className="ml-auto flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        {/* Services list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-16 text-center">
              <Server className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">
                {statusFilter === 'all' ? 'No services yet' : `No ${statusFilter} services`}
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                {statusFilter === 'all' ? "Order a service for your own use to get started." : "Try a different filter."}
              </p>
              {statusFilter === 'all' && (
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setNewOrderOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Order First Service
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 mt-2">
            {filtered.map(order => (
              <Card key={order.id} className="mb-1 border border-slate-200 hover:shadow-md transition-shadow" data-testid={`self-order-${order.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Left: product info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Server className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{order.product_name}</p>
                        <p className="text-xs text-slate-500">{order.product_category || 'Service'} · {BILLING_LABELS[order.billing_cycle] || order.billing_cycle}</p>
                      </div>
                    </div>

                    {/* Center: dates */}
                    <div className="hidden sm:flex flex-col items-center text-center">
                      {order.start_date ? (
                        <>
                          <p className="text-xs text-slate-400">Started</p>
                          <p className="text-sm font-medium">{new Date(order.start_date).toLocaleDateString('en-IN')}</p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-400 italic">Not yet activated</p>
                      )}
                    </div>
                    <div className="hidden sm:flex flex-col items-center text-center">
                      {order.renewal_date ? (
                        <>
                          <p className="text-xs text-slate-400">Renews</p>
                          <p className="text-sm font-medium">{new Date(order.renewal_date).toLocaleDateString('en-IN')}</p>
                        </>
                      ) : <p className="text-xs text-slate-400">—</p>}
                    </div>

                    {/* Right: amount + status + action */}
                    <div className="flex items-center gap-2 sm:flex-shrink-0 flex-wrap justify-end">
                      <div className="text-right">
                        <p className="font-bold text-slate-900">₹{Number(order.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-slate-400">incl. GST</p>
                      </div>
                      <StatusBadge status={order.status} />
                      {order.provisioning && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCredOrder(order); setShowCredPass(false); }}
                          data-testid={`view-creds-${order.id}`}
                          className="border-green-300 text-green-700 hover:bg-green-600 hover:border-green-600 flex items-center gap-1.5"
                        >
                          <Eye className="h-3.5 w-3.5" /> View Credentials
                        </Button>
                      )}
                      {order.status === 'pending_payment' && (
                        <span className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2.5 py-0.5">
                          Awaiting wallet top-up
                        </span>
                      )}
                      {(order.status === 'expired' || order.status === 'cancelled') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-300 text-green-700 hover:bg-green-600 hover:border-green-600 flex items-center gap-1.5"
                          onClick={() => handleRenew(order)}
                          disabled={renewingId === order.id}
                          data-testid={`renew-order-${order.id}`}
                        >
                          {renewingId === order.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <RefreshCw className="h-3.5 w-3.5" />}
                          Renew
                        </Button>
                      )}
                      {/* Invoice download — only for confirmed/active orders, not pending */}
                      {!['pending', 'pending_payment'].includes(order.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadInvoice(order)}
                          disabled={downloadingInvoice === order.id}
                          data-testid={`download-invoice-${order.id}`}
                          className="border-slate-300 text-slate-700 hover:bg-slate-600 hover:border-slate-600 flex items-center gap-1.5"
                          title="Download Invoice"
                        >
                          {downloadingInvoice === order.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <FileText className="h-3.5 w-3.5" />}
                          Invoice
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDetailOrder(order)}
                        data-testid={`view-order-${order.id}`}
                        className="text-slate-400 hover:text-blue-600"
                      >
                        <Eye className="h-4 w-4 text-slate-400 hover:text-white" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Invoices note removed — Invoice button now directly on each service card */}
      </div>

      {/* Modals */}
      <NewOrderModal
        open={newOrderOpen}
        onClose={() => setNewOrderOpen(false)}
        onSuccess={fetchOrders}
      />
      <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />

      {/* Credentials Dialog */}
      <Dialog open={!!credOrder} onOpenChange={o => { if (!o) setCredOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-600" /> Login Credentials
            </DialogTitle>
          </DialogHeader>
          {credOrder?.provisioning && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-slate-600">Service: <span className="font-semibold">{credOrder.product_name}</span></p>
              <div className="bg-green-50 border border-green-200 rounded-lg overflow-hidden">
                {[
                  { label: 'Server URL', value: credOrder.provisioning.server_url },
                  { label: 'Port',       value: credOrder.provisioning.port },
                  { label: 'Username',   value: credOrder.provisioning.username },
                  { label: 'Password',   value: credOrder.provisioning.password, secret: true },
                  { label: 'BIOS / IPMI', value: credOrder.provisioning.bios_ipmi_access },
                ].filter(f => f.value).map(f => (
                  <div key={f.label} className="flex items-center gap-3 px-4 py-2.5 border-b border-green-100 last:border-0">
                    <span className="text-xs text-green-700 w-24 flex-shrink-0">{f.label}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <code className="text-sm font-mono font-semibold text-slate-800 break-all">
                        {f.secret && !showCredPass ? '••••••••' : f.value}
                      </code>
                      {f.secret && (
                        <button onClick={() => setShowCredPass(s => !s)} className="text-xs text-green-600 underline flex-shrink-0">
                          {showCredPass ? 'Hide' : 'Show'}
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(String(f.value)); toast.success(`${f.label} copied!`); }}
                      className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0"
                    >Copy</button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400">
                Provisioned: {new Date(credOrder.provisioning.provisioned_at).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-red-500">⚠️ Keep these credentials secure. Do not share them.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
